import type {
  EmergencyActivationMethod,
  EmergencyAssignmentFirestoreRecord,
  EmergencyIncident,
  EmergencyIncidentFirestoreRecord,
  EmergencyIncidentState,
  EmergencyMonitoringContext,
} from '@src/models/interfaces/emergency.interface';
import {
  EMERGENCY_ACTIVATION_METHODS,
  EMERGENCY_INCIDENT_STATES,
  EMERGENCY_MONITORING_CONTEXTS,
} from '@src/models/interfaces/emergency.interface';
import { DateUtil } from '@src/packages/utils/dates';
import { resolveUserDisplayName } from '@src/packages/utils/user';

import { EmergencyAssignmentMapper } from './EmergencyAssignmentMapper';
import { asNumber, asRecord, asString } from './emergencyMapper.utils';

const toEnum = <T extends string>(value: unknown, values: readonly T[], fallback: T): T =>
  values.includes(value as T) ? (value as T) : fallback;

const firstString = (...values: unknown[]): string => {
  const value = values.find(item => typeof item === 'string' && item.trim());
  return typeof value === 'string' ? value.trim() : '';
};

export class EmergencyIncidentMapper {
  static fromFirestore(
    record: EmergencyIncidentFirestoreRecord,
    assignmentRecords: EmergencyAssignmentFirestoreRecord[] = [],
  ): EmergencyIncident {
    const subject = asRecord(record.subject);
    const activation = record.activation ?? {};
    const activationSettings = asRecord(activation.settings);
    const activationMethod = toEnum<EmergencyActivationMethod>(
      activationSettings.activationMethod,
      Object.values(EMERGENCY_ACTIVATION_METHODS),
      EMERGENCY_ACTIVATION_METHODS.MANUAL_PANIC,
    );
    const state = toEnum<EmergencyIncidentState>(
      record.state,
      Object.values(EMERGENCY_INCIDENT_STATES),
      EMERGENCY_INCIDENT_STATES.ACTIVE,
    );
    const responders = assignmentRecords.map(EmergencyAssignmentMapper.fromFirestore);
    const primary =
      responders.find(responder => responder.assignmentId === record.primaryAssignmentId) ??
      responders.find(responder => responder.primary) ??
      null;
    const createdAtDevice = DateUtil.toDateOrNull(record.createdAtDevice);
    const createdAt = DateUtil.toDateOrNull(record.createdAt);
    const updatedAt = DateUtil.toDateOrNull(record.updatedAt);
    // Closure contract (mobile app): `resolution` / `cancellation` maps. `resolvedBy` and
    // `canceledBy` may be a uid string, so only display-name fields are shown as a name.
    const resolutionRecord = asRecord(record.resolution);
    const cancellationRecord = asRecord(record.cancellation);
    const resolvedBy = asRecord(resolutionRecord.resolvedBy);
    const canceledBy = asRecord(cancellationRecord.canceledBy);
    const endedAt =
      DateUtil.toDateOrNull(resolutionRecord.resolvedAt) ??
      DateUtil.toDateOrNull(cancellationRecord.canceledAt) ??
      DateUtil.toDateOrNull(record.resolvedAt) ??
      DateUtil.toDateOrNull(record.canceledAt);
    const closedByName = firstString(
      resolutionRecord.resolvedByDisplayName,
      resolvedBy.displayName,
      cancellationRecord.canceledByDisplayName,
      canceledBy.displayName,
    );
    const reason = firstString(resolutionRecord.reason, cancellationRecord.reason);
    const outcome = firstString(resolutionRecord.outcome);
    const summary = firstString(resolutionRecord.summary);
    const closure = {
      closedAt: endedAt,
      ...(closedByName && { closedBy: closedByName }),
      ...(reason && { reason }),
      ...(outcome && { outcome }),
      ...(summary && { summary }),
    };
    const subjectKind = subject.kind === 'externalPerson' ? 'externalPerson' : 'linkedWorker';
    const subjectUserId =
      subjectKind === 'linkedWorker' ? asString(subject.userId, asString(record.workerId)) : '';
    const displayName =
      subjectKind === 'externalPerson'
        ? asString(subject.displayName)
        : resolveUserDisplayName(
            {
              firstName: asString(subject.firstName),
              lastName: asString(subject.lastName),
              displayName: asString(subject.displayName),
            },
            '',
          );
    const monitoringOrigin = asRecord(record.monitoringOrigin);
    const monitoringSessionId = asString(monitoringOrigin.sessionId);

    return {
      id: record.id,
      organizationId: '',
      divisionId: asString(record.divisionId),
      emergencyTypeId: asString(activation.emergencyTypeId),
      emergencyTypeName: asString(activation.emergencyTypeName),
      activationMethod,
      state,
      activatedAt: createdAtDevice ?? createdAt,
      endedAt,
      ...(state !== EMERGENCY_INCIDENT_STATES.ACTIVE && { closure }),
      createdAtDevice,
      subject:
        subjectKind === 'externalPerson'
          ? {
              kind: 'externalPerson',
              displayName,
              ...(asString(subject.externalId) && { externalId: asString(subject.externalId) }),
              ...(asString(subject.phone) && { phone: asString(subject.phone) }),
              ...(asString(subject.email) && { email: asString(subject.email) }),
              ...(asString(subject.context) && { context: asString(subject.context) }),
              source: asString(subject.source),
              sourceQuality: ['verified', 'trusted'].includes(asString(subject.sourceQuality))
                ? (subject.sourceQuality as 'verified' | 'trusted')
                : 'unverified',
            }
          : {
              kind: 'linkedWorker',
              userId: subjectUserId,
              displayName,
              ...(asString(subject.phone) && { phone: asString(subject.phone) }),
            },
      source: {
        type: subjectKind === 'externalPerson' ? asString(subject.source, 'external') : activationMethod,
        label:
          subjectKind === 'externalPerson' ? asString(subject.source, 'external') : activationMethod,
        ...(asString(subject.externalId) && { externalId: asString(subject.externalId) }),
      },
      ...(monitoringSessionId && {
        monitoringOrigin: {
          sessionId: monitoringSessionId,
          context: toEnum<EmergencyMonitoringContext>(
            monitoringOrigin.context,
            EMERGENCY_MONITORING_CONTEXTS,
            'always',
          ),
          ...(asString(monitoringOrigin.referenceId) && {
            referenceId: asString(monitoringOrigin.referenceId),
          }),
        },
      }),
      escalation: {
        currentLevel: asNumber(record.currentEscalationLevel, 0),
        currentLevelName: '',
        nextDeadlineAt: DateUtil.toDateOrNull(
          record.nextEscalationAt ?? record.nextEscalationDeadlineAt,
        ),
      },
      ...(record.primaryAssignmentId && { primaryAssignmentId: record.primaryAssignmentId }),
      response: {
        primary,
        support: responders.filter(responder => responder.assignmentId !== primary?.assignmentId),
      },
      freshness: {
        lastActivityAt: updatedAt ?? createdAtDevice ?? createdAt,
        lastLocationAt: null,
        stale: false,
      },
      allowedCommands: [],
    };
  }
}
